import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faFile, faImage } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import { generateUrls } from '@hooks/useImageLibrary'
import useAssetLibrary, { generateUrls as generateAssetLibraryUrls } from '@hooks/useAssetLibrary'

import useAssetLibraries from '@hooks/useAssetLibraries'
import useCurrentUser from '@hooks/useCurrentUser'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import AssetLibraryListItem from '../AssetLibraryListItem'

const buildPageFilterFields = (currentUserPayload: ReturnType<typeof useCurrentUser>) => {
  const { isBrandUser } = currentUserPayload

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      callbacks: {
        shouldRender: ({ brand_id }: { brand_id?: number }) => !brand_id,
      },
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      hidden: isBrandUser,
      key: 'owner_type',
      label: 'Filter by Owner Type',
      values: [
        { key: 'Agency', label: 'Agency' },
        { key: 'Brand', label: 'Brand' },
      ],
    },
    {
      callbacks: {
        shouldRender: ({ owner_type }: { owner_type?: string }) => owner_type === 'Brand',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      hidden: isBrandUser,
      key: 'owner_id',
      renderKey: 'brandId',
    },
    {
      callbacks: {
        shouldRender: ({ owner_type }: { owner_type?: string }) => owner_type === 'Agency',
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
      },
      key: 'owner_id',
      renderKey: 'agencyId',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  useLocation()

  const currentUserPayload = useCurrentUser()
  const { currentUser: { brand_id }, isBrandUser } = currentUserPayload

  const assetLibrariesPayload = useAssetLibraries({
    filters: {
      ...pageFilters,
    },
    performHttpRequests: true,
    requestOptions: {
      include_asset_library_attachments_count: true,
      include_asset_library_owner: true,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredAssetLibraries,
    filteredAssetLibrariesCount,
    hasFilteredAssetLibraries,
    loading,
  } = assetLibrariesPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const initAssetLibrary = isBrandUser ? { owner_id: brand_id, owner_type: 'Brand' } : {}
  const useAssetLibraryPayload = useAssetLibrary(initAssetLibrary)

  const {
    callbacks: { createOrEditAssetLibrary },
  } = useAssetLibraryPayload

  const { assetLibrariesIndexUrl } = generateAssetLibraryUrls(initAssetLibrary)

  const { imageLibrariesIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Asset Libraries | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            onClick={() => createOrEditAssetLibrary()}
            size="medium"
          >
            Create
          </Button>
        )}
        activeTabBarItemKey="assetLibraries"
        tabBarItems={[
          {
            href: imageLibrariesIndexUrl,
            icon: faImage,
            key: 'imageLibraries',
            title: 'Image Libraries',
          },
          {
            href: assetLibrariesIndexUrl,
            icon: faFile,
            key: 'assetLibraries',
            title: 'Document Libraries',
          },
        ]}
        title="Libraries"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredAssetLibraries && <ListBlankState />}

            {!loading && hasFilteredAssetLibraries && (
              <DashboardModule title="Document Libraries">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredAssetLibraries.map(assetLibrary => (
                      <AssetLibraryListItem
                        assetLibrary={assetLibrary}
                        key={assetLibrary.id}
                      />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredAssetLibrariesCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(currentUserPayload)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
